import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Alert, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { groupsApi } from '../../services/groups-api-client';
import {
  EMAIL,
  SMS,
  composeHelperText,
  sendOutcomeSentence,
  normalizeChannels,
  sendButtonLabel,
  shortChannelLabel,
  summarizeBroadcastAudience,
  toggleChannel,
  totalSendable,
  type BroadcastChannel,
} from '../../lib/broadcastChannels';
import { HANDED_OFF_NOTE } from '../../lib/broadcastDelivery';
import { describeRosterDrift, rosterFingerprint } from '../../lib/broadcastConfirmation';
import { audienceWireFields } from '../../lib/audienceKind';
import { plural } from '../../lib/plural';
import BroadcastHistory, { BROADCAST_HISTORY_KEY } from './broadcasts/BroadcastHistory';
import ChannelPicker from './broadcasts/ChannelPicker';
import ScheduleControls, { type SendMode } from './broadcasts/ScheduleControls';
import ReminderPlanButton from './broadcasts/ReminderPlanButton';
import ScheduledBroadcastsList from './broadcasts/ScheduledBroadcastsList';
import SendConfirmDialog from './broadcasts/SendConfirmDialog';
import { useAudienceKindSupport } from './broadcasts/useAudienceKindSupport';
import { useBroadcastRoster } from './broadcasts/useBroadcastRoster';
import { ChannelPlanSummary, ResultList } from './broadcasts/BroadcastPlanSummary';
import {
  AUDIENCES,
  AUDIENCE_ALL,
  AUDIENCE_UNCHOSEN,
  audienceIsChosen,
  audienceLabel,
} from './broadcasts/audience';
import { draftFromBroadcast, draftFromScheduled, type ComposerDraft } from '../../lib/composerDraft';
import type {
  GroupBroadcastSummary,
  GroupDetail,
  GroupScheduledBroadcast,
  SendGroupBroadcastRequest,
  SendGroupBroadcastResponse,
} from '../../types/groups';

const SUBJECT_MAX = 200;
const BODY_MAX = 5000;

export default function BroadcastsTab({ group, onOpenOverview }: { group: GroupDetail; onOpenOverview?: () => void }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [smsBody, setSmsBody] = useState('');
  // Email only until the host says otherwise. A host who never touches the
  // picker sends exactly the broadcast Studio has always sent.
  const [channels, setChannels] = useState<BroadcastChannel[]>([EMAIL]);
  const [segment, setSegment] = useState(AUDIENCE_ALL);
  /*
    Off by default on a public trip: on is the more useful send and the less
    safe one — see the field docs on SendGroupBroadcastRequest — so the host
    opts in per send, the same posture as SMS itself.

    On by default on an invite-only trip, where off is not the safe choice, it
    is the broken one. Without a token the group read returns null and the guest
    gets the 404 panel, so every guest who taps the link lands nowhere. There is
    no privacy won by sending a link that cannot open.
  */
  const [personalizeInviteLinks, setPersonalizeInviteLinks] = useState(
    () => group.accessMode === 'invite_only',
  );
  const [preview, setPreview] = useState<SendGroupBroadcastResponse | null>(null);
  const [previewKey, setPreviewKey] = useState('');
  const [sendResult, setSendResult] = useState<SendGroupBroadcastResponse | null>(null);
  // Snapshot of what the completed send ran on. The result panel outlives the
  // composer's state, and reading the live channels back would re-describe a
  // finished send every time the host ticked a box for the next one.
  const [sentChannels, setSentChannels] = useState<BroadcastChannel[]>([EMAIL]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Set when the send-time re-count disagreed with the confirmed dry run. It is
  // the refusal, not a hint: nothing went out while this is on screen.
  const [rosterDrift, setRosterDrift] = useState<string | null>(null);
  // "Send now" (untouched) or "Send at…" (ScheduleControls). Lifted here only
  // so the interactive Send button below can disarm itself in the latter mode.
  const [sendMode, setSendMode] = useState<SendMode>('now');

  const queryClient = useQueryClient();
  const broadcastMutation = useMutation(
    (request: SendGroupBroadcastRequest) => groupsApi.sendBroadcast(group.id, request),
  );
  const sending = broadcastMutation.isLoading;
  const error = (broadcastMutation.error as any)?.response?.data?.error
    || (broadcastMutation.error as Error | null)?.message
    || '';

  const smsSelected = channels.includes(SMS);
  const { supported: audienceKindSupported } = useAudienceKindSupport(group.id, segment);

  // "Send" only arms when a dry run was made for this exact send; any edit to
  // subject/body/text/audience/channels changes the key and disarms it.
  // Channels and smsBody are load-bearing here: without them, ticking SMS after
  // an email-only dry run would leave Send armed against numbers that describe
  // a different send entirely. So is the roster — the dry run counted a guest
  // list, and a list that changed is a different send however untouched the
  // composer looks.
  // personalizeInviteLinks is in here because it changes the URL, and the URL is
  // in the billed text: a token adds ~40 characters, which is the difference
  // between a one-segment dry run and a two-segment send.
  // audienceKindSupported: it changes who the API resolves the audience from
  // (send-time answers vs. the frozen list), so a probe landing after a dry run
  // is a different send.
  const composeKey = JSON.stringify([
    subject, body, smsBody, segment, channels, personalizeInviteLinks,
    rosterFingerprint(group.invitees ?? []), audienceKindSupported,
  ]);
  const armed = Boolean(preview) && previewKey === composeKey;

  // See useBroadcastRoster — the Going override is not in `group.invitees`.
  const roster = useBroadcastRoster(group.id, segment);

  const smsBodyError = smsSelected && !smsBody.trim()
    ? 'Write the text message before sending by SMS.'
    : null;
  // An unchosen audience blocks the dry run, which is what blocks the send: a
  // host cannot confirm a count that was never taken.
  const composeReady = Boolean(subject.trim())
    && Boolean(body.trim())
    && channels.length > 0
    && audienceIsChosen(segment)
    && !roster.missing
    && !smsBodyError;

  // Every number on screen comes from the dry run the API just ran, per channel.
  const audience = armed && preview ? summarizeBroadcastAudience(preview, channels) : null;
  const plans = audience?.plans ?? [];
  const sendable = totalSendable(plans);
  // The concierge API and Studio deploy separately. An API that predates
  // per-channel sends returns one flat list with no way to say which channel it
  // describes, so we say that and refuse to arm rather than split it by guess.
  const unreported = audience?.unreported ?? [];

  const buildRequest = (dryRun: boolean): SendGroupBroadcastRequest => ({
    subject,
    body,
    // Never post an empty array: the API reads empty as email-only, which would
    // email a roster the host had just unticked email for.
    channels: normalizeChannels(channels),
    // Copy for a channel we did not pick is a 400 at the API, and rightly so:
    // it is how the wrong message ships next time SMS is ticked.
    smsBody: smsSelected ? smsBody : null,
    // RSVP audiences: `audienceKind` ALONE when the API has proved it decides
    // them at send time, else today's frozen ids ([] stays [] — undefined would
    // text the whole roster). See lib/audienceKind.ts.
    ...audienceWireFields(segment, roster.guests, audienceKindSupported),
    personalizeInviteLinks,
    dryRun,
  });

  const handlePreview = async () => {
    setSendResult(null);
    setRosterDrift(null);
    try {
      const result = await broadcastMutation.mutateAsync(buildRequest(true));
      setPreview(result);
      setPreviewKey(composeKey);
    } catch {
      // react-query exposes the failure through broadcastMutation.error.
    }
  };

  const handleSend = async () => {
    setConfirmOpen(false);
    const confirmed = preview;
    if (!confirmed) return;
    setRosterDrift(null);
    try {
      // The confirmed numbers counted the roster as it stood at preview time,
      // and the API re-reads it when the send lands. A co-host importing guests
      // in another tab moves the audience without touching anything here, so
      // count again and refuse rather than text twenty people whose numbers the
      // host never saw in a confirmation.
      const recheck = await broadcastMutation.mutateAsync(buildRequest(true));
      const drift = describeRosterDrift(confirmed, recheck, channels);
      if (drift) {
        setRosterDrift(drift);
        setPreview(recheck);
        setPreviewKey(composeKey);
        return;
      }
      const result = await broadcastMutation.mutateAsync(buildRequest(false));
      setSentChannels(normalizeChannels(channels));
      setSendResult(result);
      setPreview(null);
      setPreviewKey('');
      queryClient.invalidateQueries(BROADCAST_HISTORY_KEY(group.id));
    } catch {
      // react-query exposes the failure through broadcastMutation.error.
    }
  };

  // "Reuse" loads a past broadcast — or a reminder Reschedule cancelled as possibly sent — back into the composer; the host still dry-runs and
  // confirms before anything sends. ONE state path serves both sources (see `ComposerDraft`).
  const loadDraft = (draft: ComposerDraft) => {
    setSubject(draft.subject);
    setBody(draft.body);
    // Channels and the text body are restored together: one without the other re-sends an email+SMS broadcast as email only.
    setChannels(normalizeChannels(draft.channels));
    setSmsBody(draft.smsBody);
    // The audience is NOT restored unless the source can prove it (a null segment on a sent broadcast is ambiguous): see `lib/composerDraft`.
    setSegment(draft.audience);
    // Personalised links change the rendered text, hence the idempotency key: restore the original's setting (see `ComposerDraft`).
    setPersonalizeInviteLinks(draft.personalizeInviteLinks ?? group.accessMode === 'invite_only');

    setPreview(null);
    setPreviewKey('');
    setSendResult(null);
    setRosterDrift(null);
    broadcastMutation.reset(); // clear any stale error/preview banner from a prior attempt
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleReuse = (past: GroupBroadcastSummary) => loadDraft(draftFromBroadcast(past));
  const handleReuseScheduled = (row: GroupScheduledBroadcast) => loadDraft(draftFromScheduled(row));

  const sentAudience = sendResult ? summarizeBroadcastAudience(sendResult, sentChannels) : null;

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={800}>Compose broadcast</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          {composeHelperText(channels)}
          {' '}Variables: {'{name}'}, {'{title}'}, {'{location}'}, {'{url}'}.
        </Typography>
        {!group.published ? (
          <Alert severity="warning" sx={{ mb: 2 }}>Publish this group before sending broadcasts.</Alert>
        ) : null}
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value.slice(0, SUBJECT_MAX))}
            inputProps={{ maxLength: SUBJECT_MAX }}
            helperText={`${subject.length}/${SUBJECT_MAX}`}
          />
          <TextField
            fullWidth
            multiline
            minRows={5}
            label="Email message"
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
            inputProps={{ maxLength: BODY_MAX }}
            helperText={`${body.length}/${BODY_MAX} · Variables: {name} {title} {location} {url}`}
          />
          <ChannelPicker
            channels={channels}
            onToggle={(channel, on) => setChannels((prev) => toggleChannel(prev, channel, on))}
            smsBody={smsBody}
            onSmsBody={setSmsBody}
            smsBodyError={smsBodyError}
            disabled={sending}
            personalizeInviteLinks={personalizeInviteLinks}
            onPersonalizeInviteLinks={setPersonalizeInviteLinks}
            inviteOnly={group.accessMode === 'invite_only'}
            trip={{ groupId: group.id, title: group.title, slug: group.slug, locationName: group.locationName }}
          />
          <TextField
            select
            label="Audience"
            value={segment}
            onChange={(e) => setSegment(e.target.value)}
            error={!audienceIsChosen(segment) || roster.missing}
            helperText={
              !audienceIsChosen(segment)
                ? 'Reuse cannot tell who this went to last time. Pick the audience.'
                : roster.hint || undefined
            }
            sx={{ maxWidth: 280 }}
          >
            {!audienceIsChosen(segment) ? (
              <MenuItem value={AUDIENCE_UNCHOSEN} disabled>Choose an audience</MenuItem>
            ) : null}
            {AUDIENCES.map((audienceOption) => (
              <MenuItem key={audienceOption.value} value={audienceOption.value}>{audienceOption.label}</MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            disabled={sending || !composeReady}
            onClick={handlePreview}
          >
            {sending ? 'Working...' : 'Preview (dry run)'}
          </Button>
          <Button
            variant="contained"
            disabled={
              sending || !armed || !group.published || sendable === 0 || unreported.length > 0
              // "Send at…" replaces this button with ScheduleControls' own
              // Schedule button below — an armed dry run must not double as a
              // way to send immediately once the host has chosen to schedule.
              || sendMode !== 'now'
            }
            onClick={() => setConfirmOpen(true)}
          >
            {/* Before a dry run there is no honest per-channel number, and one
                blended estimate is the thing this tab exists to stop showing. */}
            {armed ? sendButtonLabel(plans) : 'Send'}
          </Button>
        </Stack>
        {preview && !armed ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            The message changed since the last preview. Run another dry run to re-arm Send.
          </Alert>
        ) : null}
        {rosterDrift ? (
          // A send that was stopped, stated as such. The alternative — sending
          // anyway and reporting the larger number afterwards — is how people
          // get texted against a confirmation that never named them.
          <Alert severity="warning" sx={{ mt: 2 }}>{rosterDrift}</Alert>
        ) : null}
        <ScheduleControls
          group={group}
          mode={sendMode}
          onModeChange={setSendMode}
          subject={subject}
          body={body}
          smsBody={smsBody}
          smsSelected={smsSelected}
          channels={channels}
          segment={segment}
          personalizeInviteLinks={personalizeInviteLinks}
          roster={roster}
          composeReady={composeReady}
        />
        <ReminderPlanButton group={group} onOpenOverview={onOpenOverview} />
      </Paper>

      {preview && armed ? (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" fontWeight={800} sx={{ mb: 1.5 }}>
            Preview · {plural(preview.requestedCount, 'guest')} targeted ({audienceLabel(segment)})
          </Typography>
          {unreported.length > 0 ? (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              The concierge API reported no per-channel result for{' '}
              {unreported.map(shortChannelLabel).join(' and ')}. Sending is blocked until it does —
              a single blended count can't say who would be texted.
            </Alert>
          ) : null}
          <ChannelPlanSummary plans={plans} />
          <ResultList results={preview.results} />
        </Paper>
      ) : null}

      {sendResult && sentAudience ? (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          {/* Success is "something went out", not "we attempted something": a
              plan whose rows all failed still reports a full reachable count. */}
          <Alert
            severity={sentAudience.plans.some((plan) => plan.sent > 0) ? 'success' : 'warning'}
            sx={{ mb: 1.5 }}
          >
            {sendOutcomeSentence(sentAudience.plans)} · {plural(sendResult.requestedCount, 'guest')} targeted.
            <Typography variant="caption" display="block">{HANDED_OFF_NOTE}</Typography>
          </Alert>
          {/* The flat skippedCount is deliberately not shown. Rows are per
              (guest, channel) now, so it cannot say whether 20 means twenty
              guests or ten guests missed on two channels. */}
          <ChannelPlanSummary plans={sentAudience.plans} variant="sent" />
          <ResultList results={sendResult.results} />
        </Paper>
      ) : null}

      <ScheduledBroadcastsList group={group} onReuseScheduled={handleReuseScheduled} />

      <BroadcastHistory groupId={group.id} timeZone={group.timeZone} onReuse={handleReuse} />

      <SendConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleSend}
        plans={plans}
        segment={segment}
        smsSelected={smsSelected}
      />
    </Stack>
  );
}
